import { SeoMeta } from './components/site/SeoMeta';
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import CookieBanner from "./components/CookieBanner";

// Loading Component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
    <div className="animate-pulse text-[#0E7490] text-xl">Laden...</div>
  </div>
);

// Lazy loaded pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const NachweisAbrufPage = lazy(() => import("./pages/NachweisAbrufPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AdvisorRegisterPage = lazy(() => import("./pages/AdvisorRegisterPage"));
const PatientDashboard = lazy(() => import("./pages/PatientDashboard"));
const AdvisorDashboard = lazy(() => import("./pages/AdvisorDashboard"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const PartnerLandingPage = lazy(() => import("./pages/PartnerLandingPage"));
const PartnerTermsPage = lazy(() => import("./pages/PartnerTermsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const BookAppointment = lazy(() => import("./pages/BookAppointment"));
const VideoRoom = lazy(() => import("./pages/VideoRoom"));
const VideoConsultationPage = lazy(() => import("./pages/VideoConsultationPage"));
const VideoThanksPage = lazy(() => import("./pages/VideoThanksPage"));
const GuestVideoPage = lazy(() => import("./pages/GuestVideoPage"));
const AdvisorConsultationRoom = lazy(() => import("./pages/AdvisorConsultationRoom"));
const ConsultationForm = lazy(() => import("./pages/ConsultationForm"));
const SignDocument = lazy(() => import("./pages/SignDocument"));
const MyDocuments = lazy(() => import("./pages/MyDocuments"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ImpressumPage = lazy(() => import("./pages/ImpressumPage"));
const DatenschutzPage = lazy(() => import("./pages/DatenschutzPage"));
const AGBPage = lazy(() => import("./pages/AGBPage"));
const VideoTestPage = lazy(() => import("./pages/VideoTestPage"));
const AdvisorAvailabilityPage = lazy(() => import("./pages/AdvisorAvailabilityPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const AdminCalendarPage = lazy(() => import("./pages/AdminCalendarPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminSettingsPage = lazy(() => import("./pages/AdminSettingsPage"));

// Neue SEO-Seiten
const UeberUnsPage = lazy(() => import("./pages/UeberUnsPage"));
const KontaktPage = lazy(() => import("./pages/KontaktPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const RatgeberPage = lazy(() => import("./pages/RatgeberPage"));
const RatgeberArtikelPage = lazy(() => import("./pages/RatgeberArtikelPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-pulse-soft text-[#4A7C59] text-xl">Laden...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// /beratung/:id – Kunde (Technik-Check + Raum), Berater/Admin (Moderator-Raum), Gast per ?gast=<token>
const BeratungRouter = () => {
  const { appointmentId } = useParams();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const gast = params.get("gast");
  if (gast) return <GuestVideoPage token={gast} appointmentId={appointmentId} />;
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=/beratung/${appointmentId}`} replace />;
  if (user.role === "advisor" || user.role === "admin") return <AdvisorConsultationRoom />;
  return <VideoConsultationPage />;
};

const LegacyVideoRedirect = () => <Navigate to={`/beratung/${useParams().appointmentId}`} replace />;

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/login" replace />;
  
  if (user.role === "advisor" || user.role === "admin") {
    return <AdvisorDashboard />;
  }
  if (user.role === "partner") return <Navigate to="/partner" replace />;
  
  return <PatientDashboard />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SeoMeta />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/advisor" element={<AdvisorRegisterPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/nachweis/:token" element={<NachweisAbrufPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Neue SEO-Seiten */}
            <Route path="/ueber-uns" element={<UeberUnsPage />} />
            <Route path="/kontakt" element={<KontaktPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/fuer-pflegedienste" element={<PartnerLandingPage />} />
            <Route path="/partner-bedingungen" element={<PartnerTermsPage />} />
            <Route path="/partner" element={<ProtectedRoute allowedRoles={["partner"]}><PartnerDashboard /></ProtectedRoute>} />
            <Route path="/ratgeber" element={<RatgeberPage />} />
            <Route path="/ratgeber/:slug" element={<RatgeberArtikelPage />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/book-appointment" 
            element={
              <ProtectedRoute>
                <BookAppointment />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/video/:roomId" 
            element={
              <ProtectedRoute>
                <VideoRoom />
              </ProtectedRoute>
            } 
          />
          
          <Route path="/video-beratung/:appointmentId" element={<LegacyVideoRedirect />} />
          <Route path="/video-gast/:token" element={<GuestVideoPage />} />
          <Route path="/beratung/:appointmentId" element={<BeratungRouter />} />
          <Route path="/beratung/:appointmentId/danke" element={<VideoThanksPage />} />
          <Route path="/beratung/:appointmentId/ende" element={<ProtectedRoute allowedRoles={["advisor", "admin"]}><AdvisorConsultationRoom ended /></ProtectedRoute>} />

          <Route 
            path="/consultation/:appointmentId" 
            element={
              <ProtectedRoute allowedRoles={["advisor", "admin"]}>
                <ConsultationForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/sign/:consultationId" 
            element={
              <ProtectedRoute>
                <SignDocument />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/my-documents" 
            element={
              <ProtectedRoute>
                <MyDocuments />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminSettingsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/video-test" 
            element={
              <ProtectedRoute>
                <VideoTestPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/availability" 
            element={
              <ProtectedRoute allowedRoles={["advisor", "admin"]}>
                <AdvisorAvailabilityPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/feedback/:appointmentId" 
            element={
              <ProtectedRoute allowedRoles={["patient"]}>
                <FeedbackPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/calendar" 
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminCalendarPage />
              </ProtectedRoute>
            } 
          />
          
          {/* 404 - Catch all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
